import React, { forwardRef, useCallback, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { bubblePath } from '../utils/bubblePath';
interface HeroChatBubbleProps {
  text: string;
  /** Newest note: full size with the iMessage tail pointing at Pixel. Older notes shrink to 90%. */
  main: boolean;
  reduce: boolean;
  /**
   * Corner chat: newest note keeps the stacked (small) size, but gets the tail mirrored to its
   * bottom-right so it points at Pixel sitting below-right.
   */
  tailRight?: boolean;
}

/** iMessage received-bubble grey */
const FILL = '#E9E9EB';
/** iMessage: 18pt radius on 17pt text */
const RADIUS_EM = 1.27; // half of a one-line bubble's height, so single lines stay a full pill
/** One soft spring for every part of the stack, so growing, shrinking and shifting move together */
const SPRING = {
  type: 'spring',
  stiffness: 300,
  damping: 30,
  mass: 0.8
} as const;
const EXIT_EASE = [0.23, 1, 0.32, 1] as const;
/** Older notes in the stack: drawn at this share of the newest note's size (≈ the reply chips' text size) */
const STACKED_SCALE = 0.74;

/**
 * One of Pixel's notes in the hero — a plain grey iMessage bubble. Laid out at full size and
 * scaled from its bottom-left corner; the outer box animates to the scaled size, so the stack
 * reflows smoothly (no text reflow, no jumps) as notes are pushed up.
 */
export const HeroChatBubble = forwardRef<HTMLDivElement, HeroChatBubbleProps>(function HeroChatBubble({
  text,
  main,
  reduce,
  tailRight = false
}, forwardedRef) {
  const [box, setBox] = useState({
    w: 0,
    h: 0,
    r: 0
  });
  const observer = useRef<ResizeObserver | null>(null);
  const setRef = useCallback((el: HTMLDivElement | null) => {
    if (typeof forwardedRef === 'function') forwardedRef(el);else if (forwardedRef) forwardedRef.current = el;
    observer.current?.disconnect();
    if (!el) return;
    const measure = () => {
      const fontSize = parseFloat(getComputedStyle(el).fontSize) || 16;
      const h = el.offsetHeight;
      // Single lines stay a full pill at either size
      setBox({
        w: el.offsetWidth,
        h,
        r: Math.min(fontSize * RADIUS_EM, h / 2)
      });
    };
    observer.current = new ResizeObserver(measure);
    observer.current.observe(el);
    measure();
  }, [forwardedRef]);

  // Stacked notes keep their exact layout (same text size, same line breaks) and are only *scaled* down
  // from their tail corner — switching text size made them re-wrap and jump mid-animation
  // Phones: stacked notes stay full size (shrinking makes them hard to read on a small screen)
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const sync = () => setNarrow(mq.matches);
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  const scale = tailRight || main || narrow ? 1 : STACKED_SCALE;
  // tailRight: small text size, tail drawn then mirrored to the right side
  const big = !tailRight;
  // Tail only on the newest note (stays drawn while it shrinks, then fades with the scale change)
  const d = box.w > 0 ? bubblePath(box.w, box.h, box.r, main || tailRight) : '';
  const originX = tailRight ? 1 : 0;
  const t = reduce ? {
    duration: 0
  } : SPRING;
  // First measurement snaps the footprint into place (no grow-from-zero wobble); later size changes spring
  const [sized, setSized] = useState(false);
  React.useEffect(() => {
    if (box.w > 0 && !sized) {
      const id = requestAnimationFrame(() => setSized(true));
      return () => cancelAnimationFrame(id);
    }
  }, [box.w, sized]);
  const sizeT = sized ? t : {
    duration: 0
  };
  const outerT = {
    ...t,
    width: sizeT,
    height: sizeT
  };
  return (
    // Outer: takes the bubble's *scaled* footprint so neighbours slide into place
    <motion.div layout={reduce ? false : 'position'} className="relative" initial={reduce ? false : {
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0,
      width: box.w * scale,
      height: box.h * scale
    }}
    // Leaving the top of the stack: drifts up and softly dissolves out of focus
    exit={reduce ? {
      opacity: 0,
      transition: {
        duration: 0
      }
    } : {
      opacity: 0,
      y: -18,
      scale: 0.96,
      filter: 'blur(6px)',
      transition: {
        opacity: {
          duration: 0.26,
          ease: 'easeOut'
        },
        y: {
          duration: 0.3,
          ease: EXIT_EASE
        },
        scale: {
          duration: 0.3,
          ease: EXIT_EASE
        },
        filter: {
          duration: 0.26,
          ease: 'easeOut'
        }
      }
    }} transition={outerT} style={{
      originX,
      originY: 1
    }} data-id="element-95" data-mp="1sm30km3032sa">
      {/* Inner: the real bubble at full size, scaled from its tail corner */}
      <motion.div ref={setRef} className={`absolute bottom-0 w-max font-medium tracking-[-0.015em] text-black ${tailRight ? 'right-0' : 'left-0'} ${big ?
      // Long notes wrap onto several lines instead of running off screen (--chat-room = space to the right)
      'max-w-[min(420px,var(--chat-room,420px),calc(100vw-48px))] text-[clamp(1.05rem,1.45vw,1.55rem)]' :
      // Same size as the blue reply chips
      'max-w-[min(340px,var(--chat-room,340px),calc(100vw-48px))] text-[clamp(0.85rem,1vw,1.125rem)]'}`} initial={reduce ? false : {
        scale: 0.86
      }} animate={{
        scale
      }} transition={t} style={{
        originX,
        originY: 1
      }} data-id="element-96" data-mp="3mzx2usl8yguz">
        {/* Shape: one flat grey path for body and tail (mirrored when the tail sits right) */}
        {d && <svg aria-hidden="true" width={box.w} height={box.h} viewBox={`0 0 ${box.w} ${box.h}`} className={`pointer-events-none absolute left-0 top-0 overflow-visible ${tailRight ? '-scale-x-100' : ''}`} data-id="element-97" data-mp="2h9wptt3fvqvj">
            <path d={d} fill={FILL} data-id="element-98" data-mp="37hb2ytslraje" />
          </svg>}

        {/* iMessage text metrics */}
        <p
        // Always left-aligned, even inside centred sections
        className={`relative m-0 text-left leading-[1.29] [text-wrap:pretty] ${big ? 'px-[1.05em] pb-[0.64em] pt-[0.6em]' : 'px-[0.95em] py-[0.42em]'}`} data-id="element-99" data-mp="32j9fzxs22r2n">
          <span className="sr-only" data-id="element-100" data-mp="2orl6815a4eou">Pixel: </span>
          {text}
        </p>
      </motion.div>
    </motion.div>
  );
});